import {
  AiActionKind,
  AiNarrativeContext,
  AiPromptStatusResponse,
  AiPromptTemplate,
} from './types';
import { getPlainText } from './utils';

// Field names confirmed against the endpoints.

const ACTION_FIELD = 'ai_action';

const TEMPLATE_TEXT_FIELD = 'prompt';

// null while the job runs, the generated text once it is done.
const GENERATED_TEXT_FIELD = 'response';

const FAILED_STATUSES = [
  'failed',
  'failure',
  'error',
  'revoked',
  'cancelled',
  'canceled',
  'timeout',
];

const PENDING_STATUSES = [
  'pending',
  'queued',
  'created',
  'submitted',
  'started',
  'running',
  'processing',
  'in_progress',
  'in progress',
  'retry',
];

function readString(
  source: Record<string, unknown>,
  field: string,
): string | null {
  const value = source[field];
  return typeof value === 'string' && value.trim() ? value : null;
}

export function pickTemplate(
  items: AiPromptTemplate[],
  actionKind: AiActionKind,
): string {
  const list = items || [];
  const match = list.find(
    item => readString(item, ACTION_FIELD) === actionKind,
  );

  if (!match) {
    // Unlabelled items are reported by their keys, to say where to look.
    const seen = list
      .map(
        item =>
          readString(item, ACTION_FIELD) || `{${Object.keys(item).join(',')}}`,
      )
      .join(', ');
    throw new Error(
      `No AI prompt template for "${actionKind}" (available: ${seen || 'none'})`,
    );
  }

  const text = readString(match, TEMPLATE_TEXT_FIELD);
  if (!text) {
    throw new Error(
      `AI prompt template "${actionKind}" carries no ${TEMPLATE_TEXT_FIELD} (fields: ${Object.keys(
        match,
      ).join(', ')})`,
    );
  }
  return text;
}

// Placeholder -> value mapping. The report and the templates use different words
// for the same things (year/period, csp_code/project_code), so both are offered.
// Ambiguous one-word names are deliberately absent: a template asking for {text}
// must fail by name rather than silently receive the wrong string.
export function promptVariables(
  context: AiNarrativeContext,
  extras: { currentContent?: string; selectedText?: string } = {},
): Record<string, string | number> {
  const plainContent = getPlainText(extras.currentContent || '');

  const variables: Record<string, string | number> = {
    year: context.period || '',
    period: context.period || '',
    csp_code: context.projectCode || '',
    project_code: context.projectCode || '',
    country: context.country || '',
    country_name: context.country || '',
    narrative_type: context.narrativeType,
    narrative_title: context.narrativeTitle,
    section_name: context.sectionTitle,
    section_title: context.sectionTitle,
    word_limit: context.wordLimit,
    narrative_content: plainContent,
    current_content: plainContent,
  };

  // Only a selection-based action can resolve these.
  if (extras.selectedText !== undefined) {
    variables.selected_text = extras.selectedText;
  }

  return variables;
}

function statusIn(status: unknown, values: string[]): boolean {
  return (
    typeof status === 'string' && values.includes(status.trim().toLowerCase())
  );
}

export function isFailedStatus(status: unknown): boolean {
  return statusIn(status, FAILED_STATUSES);
}

// Still pending means still pending, even if the payload echoes the prompt back.
export function isPendingStatus(status: unknown): boolean {
  return statusIn(status, PENDING_STATUSES);
}

export function readGeneratedText(
  payload: AiPromptStatusResponse,
): string | null {
  return payload ? readString(payload, GENERATED_TEXT_FIELD) : null;
}

// Blank-line separated blocks become paragraphs. Inline markdown is untouched.
export function textToParagraphs(text: string): string {
  return (text || '')
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n+/)
    .map(block => block.trim())
    .filter(Boolean)
    .map(block => `<p>${block.replace(/\n+/g, ' ')}</p>`)
    .join('');
}
